import type { components } from "@/api-schema";
import { Input } from "@/components/ui/input";
import { CheckIcon, Search, XIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCategorizeRoles } from "../_hooks/use-categorize-roles";
import { useFilteredRoles } from "../_hooks/use-filtered-roles";
import { RoleSection } from "./role-section";

type AppRole = components["schemas"]["AppRole"];
type AppRoleAttribute = components["schemas"]["AppRoleAttribute"];

const SHOW_SEARCH_THRESHOLD = 10;

export interface ApplicationAppRolesSubComponentProps {
  application: components["schemas"]["Application"];
  selected: AppRole[];
  attributesByUuid: Map<string, AppRoleAttribute>;
}

export function ApplicationAppRolesSubComponent({
  application,
  selected,
  attributesByUuid,
}: ApplicationAppRolesSubComponentProps) {
  const { t } = useTranslation(["ram", "masterdata", "common"]);
  const [searchTerm, setSearchTerm] = useState("");
  const { assignedRoles, unassignedRoles, assignedAttributes } = useCategorizeRoles(
    application,
    selected,
    attributesByUuid,
  );

  const totalCount = assignedRoles.length + assignedAttributes.length + unassignedRoles.length;
  const shouldShowSearch = totalCount > SHOW_SEARCH_THRESHOLD;

  const {
    filteredRoles: assignedFilteredRoles,
    filteredAttributes: assignedFilteredAttributes,
    totalFiltered: assignedFiltered,
  } = useFilteredRoles(assignedRoles, assignedAttributes, searchTerm);
  const { filteredRoles: unassignedFilteredRoles, totalFiltered: unassignedFiltered } = useFilteredRoles(
    unassignedRoles,
    [],
    searchTerm,
  );

  return (
    <div className="p-2">
      {shouldShowSearch && (
        <div className="mb-4">
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2 transform" />
            <Input
              placeholder={t("masterdata:Search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
      )}
      <div className="space-y-4">
        {(assignedRoles.length > 0 || assignedAttributes.length > 0) && assignedFiltered > 0 && (
          <RoleSection
            icon={CheckIcon}
            title={t("ram:AssignedRolesCount", { count: assignedRoles.length + assignedAttributes.length })}
            roles={assignedFilteredRoles}
            attributes={assignedFilteredAttributes}
          />
        )}

        {unassignedRoles.length > 0 && unassignedFiltered > 0 && (
          <RoleSection
            icon={XIcon}
            title={t("ram:UnassignedRolesCount", { count: unassignedRoles.length })}
            roles={unassignedFilteredRoles}
            attributes={[]}
          />
        )}

        {searchTerm && assignedFiltered === 0 && unassignedFiltered === 0 && totalCount > 0 && (
          <div className="text-muted-foreground text-center text-xs">{t("common:NoResultsFound")}</div>
        )}
      </div>
    </div>
  );
}
